import React, { useState } from 'react';
import { Observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import AppBar from '@material-ui/core/AppBar';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import defaultLogo from '../../assets/images/logo.svg';
import { performLogout } from '../../stores/api';
import { getMenus } from './configs';
import MainMenu from './MainMenu';
import SubMenu from './SubMenu';

const useStyles = makeStyles((theme) => ({
  appBar: {
    zIndex: 1,
  },
  mainHeaderContainer: {
    height: 53,
    backgroundColor: theme.palette.primary.main,
    padding: '0 24px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
  },
  avatarContainer: {
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  avatarImage: {
    width: 32,
    height: 32,
  },
  logoLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 120,
    height: 40,
    flexShrink: 0,
  },
  logoImg: {
    height: 40,
    maxWidth: 120,
    objectFit: 'contain',
  },
  userName: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#fff',
  },
  userNameBlur: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'transparent',
    textShadow: '0 0 10px rgba(255, 255, 255, 0.6)',
  },
  avatarBlur: {
    filter: 'blur(5px)',
  },
}));

const Header = ({ userStore, isBlur }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenMenu = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await performLogout(import.meta.env.REACT_APP_OAUTH_URL);
    window.location.href = `${import.meta.env.REACT_APP_OAUTH_URL}/login?logout=true&next=${encodeURI(window.location.href)}`;
  };

  return (
    <Observer render={() => {
      // Explicitly read permission-related observables so MobX tracks them.
      // These are used inside disabled() callbacks in getMenus configs,
      // which run later in HeaderMenu (outside Observer scope).
      // Without reading them here, MobX won't re-render when they change.
      const _canShowDashboards = userStore.canShowDashboards;
      const _isClaimsEnabled = userStore.isClaimsEnabled;
      const _isReadOnly = userStore.isReadOnly;
      const _isStoreUser = userStore.isStoreUser;

      const menus = getMenus(userStore);
      const useClientLogo = userStore.clientSettings?.settings?.use_client_logo === 'true';

      // Resolve logo: placeholder while loading, client logo if enabled, otherwise default
      const clientLogo = !userStore.clientSettings ? null
        : useClientLogo ? (userStore.logo || null)
        : defaultLogo;

      const userName = userStore.user?.name || 'Anonymous';
      const companyName = userStore.client?.company;
      const logoUrl = userStore.logo || '';
      const shouldBlurLogo = isBlur && useClientLogo;

      return (
        <Box display="flex" flexDirection="column">
          <AppBar position="static" elevation={0} className={classes.appBar}>
            <Box>
              <Box className={classes.mainHeaderContainer}>
                <a href="/" data-testid="header-logo" className={classes.logoLink}>
                  {clientLogo ? (
                    <img src={clientLogo} alt="logo" className={`${classes.logoImg} ${shouldBlurLogo ? classes.avatarBlur : ''}`} />
                  ) : (
                    <div className={classes.logoImg} />
                  )}
                </a>

                <MainMenu menus={menus} />

                <Box
                  onClick={handleOpenMenu}
                  className={classes.avatarContainer}
                  data-testid="header-account-menu-trigger"
                >
                  <Typography className={isBlur ? classes.userNameBlur : classes.userName} data-testid="header-user-name">
                    {userName}
                  </Typography>
                  <Avatar
                    src={logoUrl}
                    className={`${classes.avatarImage} ${isBlur ? classes.avatarBlur : ''}`}
                    data-testid="header-avatar"
                  />
                  <Typography className={isBlur ? classes.userNameBlur : classes.userName} data-testid="header-company-name">
                    {companyName}
                  </Typography>
                  {anchorEl ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                </Box>
              </Box>

              <SubMenu menus={menus} />
            </Box>
          </AppBar>

          <Menu
            id="account-menu"
            anchorEl={anchorEl}
            keepMounted
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            data-testid="header-account-menu"
          >
            {!userStore.isReadOnly && (
              <MenuItem component={Link} to="/settings" onClick={handleCloseMenu} data-testid="header-menu-settings" style={{ textDecoration: 'none', color: 'inherit' }}>
                Settings
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout} data-testid="header-menu-logout">
              Logout
            </MenuItem>
          </Menu>
        </Box>
      );
    }} />
  );
};

export default Header;
